'use client'
import { PageData } from "@/apis/type"
import { PropsWithClassname } from "@/types"
import { Empty } from "@/ui/Empty/Empty"
import { As, cn, forwardRef } from "@heroui/react"
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query"
import { useScroll } from "framer-motion"
import { debounce } from "lodash"
import { Fragment, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react"
import { SkeletonCard } from "./SkeletonCard"


export type ScrollListProps<T = any>
  = PropsWithClassname
  & {
    scrollCotnainer: RefObject<Element | null>
    scrollTarget?: RefObject<Element | null>
    data: InfiniteData<PageData<T>>
    isFetching?: boolean
    loading?: ReactNode
    hiddenLoading?: boolean
    fetchNextPage: ReturnType<typeof useInfiniteQuery>['fetchNextPage']
    hasNextPage: boolean
    childSelector?: string
    as?: As
    empty?: ReactNode
    skeleton?: ReactNode
  } & { children: ((item: T) => React.ReactNode) | React.ReactNode }


export const ScrollLoadList = forwardRef<"div", ScrollListProps>(({
  className,
  scrollCotnainer,
  scrollTarget,
  data,
  isFetching,
  fetchNextPage,
  hasNextPage,
  loading,
  hiddenLoading,
  children,
  empty,
  childSelector = 'div',
  as,
  skeleton,
  ...props }: ScrollListProps, ref) => {

  const { scrollY } = useScroll({
    container: scrollCotnainer as RefObject<HTMLElement | null>,
    layoutEffect: false
  })

  const [cardHeight, setCardHeight] = useState(0)
  // 一行能存放卡片的数量
  const [fullCardNumber, setfullCardNumber] = useState(0)
  const container = useRef<Element>(null!)


  // 计算卡片高度&一行摆放卡片的数量
  useEffect(() => {
    const computedCardWith = () => {
      setCardHeight(container.current?.querySelector(childSelector)?.clientHeight!)
    }

    const computedFullCardNum = () => {
      const el = container.current as HTMLElement | null
      if (!el) return
      // 用网格实际列数。卡片内部的 div 比格子窄，按它来除会多出一截骨架。
      const tracks = getComputedStyle(el).gridTemplateColumns
      if (tracks && tracks !== 'none') {
        const count = tracks.split(' ').filter(part => part && part !== '0px').length
        if (count > 0) {
          setfullCardNumber(count)
          return
        }
      }
      const card = el.querySelector(childSelector) as HTMLElement | null
      const cardWidth = card?.getBoundingClientRect().width || 0
      if (cardWidth <= 0 || el.clientWidth <= 0) return
      const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 0
      setfullCardNumber(Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap))))
    }

    const onResizeFn = debounce(() => {
      computedCardWith()
      computedFullCardNum()
    }, 300)

    computedCardWith()
    computedFullCardNum()
    window.addEventListener('resize', onResizeFn)

    return () => window.removeEventListener('resize', onResizeFn)

  }, [data, childSelector])

  // 下拉加载函数
  useEffect(() => {
    const unsub = scrollY.on('change', (val) => {

      const el = ((scrollTarget || container) as RefObject<HTMLElement | null>).current!


      const offset = (el?.scrollHeight - scrollCotnainer.current?.clientHeight!) - val
      if (!isFetching && offset <= cardHeight * 1.5) {

        if (hasNextPage) fetchNextPage()
      }
    })

    // 没有滚动条多加载一页
    if (scrollCotnainer.current) {
      const container = scrollCotnainer.current
      if (container.clientHeight >= container.scrollHeight && !isFetching && hasNextPage) {
        fetchNextPage()
      }
    }

    return () => unsub()
  }, [isFetching, cardHeight])


  // 获取数据的长度
  const dataLength = useMemo(() => {
    return data?.pages?.map((item: any) => item.length ?
      item.length :
      item?.data?.length || 0).reduce((a, b) => a + b, 0)
  }, [data])

  // 正好排满一行就另起一行骨架；否则只补满当前行剩下的空位
  const offsetNum = useMemo(() => {
    if (fullCardNumber <= 0) return 0
    const remainder = dataLength % fullCardNumber
    return remainder === 0 ? fullCardNumber : fullCardNumber - remainder
  }, [fullCardNumber, dataLength])


  const isEmpty = useMemo(() => {

    return data?.pages?.map(page => Array.isArray(page) ? page : page?.data || []).flat().length === 0
  }, [data])

  const Component = as || 'div'


  if (isEmpty) return empty || <Empty />

  return (
    <Component
      ref={(node) => {
        container.current = node as any
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node

      }}
      className={cn("pb-[20px]", className)}
      {...props}
    >
      {
        typeof children === 'function' ?
          data?.pages.map(page =>
            page.data?.map(item => children(item)
            ))
          : children
      }
      {/* 加载时骨架图 如果父组件被渲染成了nextui的列表，则不能出现除了ListboxItem之外的元素 */}
      {
        hiddenLoading || isFetching && (loading || (
          new Array(offsetNum || 0).fill(0).map((item, i) => <Fragment key={i}>{skeleton || <SkeletonCard />}</Fragment>)
        ))
      }

    </Component>
  )
})